/**
 * DashboardPage — campaign overview at /a/arche_advertising_optimization/:
 * campaign counts and budgets, then the Performance section (spend, clicks,
 * visits, trials, accounts, keywords, search terms, landing pages and the
 * tracking check).
 */

import { Link } from 'react-router-dom';
import { useAdoptCampaigns } from '../hooks/useAdvertisingOptimization';
import { ARCHE_PATH } from '../constants';
import { PerformanceReport } from '../components/PerformanceReport';

interface Props {
  holderId: string;
}

function centsToUsd(cents: number): string {
  return `$${(cents / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

export function DashboardPage({ holderId }: Props) {
  const campaignsQuery = useAdoptCampaigns(holderId);

  if (campaignsQuery.isLoading) {
    return <div className="p-6 text-muted-foreground">Loading…</div>;
  }

  const campaigns = campaignsQuery.data ?? [];
  const active = campaigns.filter((c) => c.status === 'active');
  const totalActiveBudget = active.reduce((sum, c) => sum + c.totalBudgetCents, 0);
  const dailyCeiling = active.reduce((sum, c) => sum + c.dailyBudgetCeilingCents, 0);

  return (
    <div className="p-6 max-w-6xl mx-auto flex flex-col gap-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">AdOpt - Advertising Optimization</h1>
          <p className="text-sm text-muted-foreground">
            Generate, deploy, measure, and continuously optimize ad campaigns.
          </p>
        </div>
        <Link
          to={`${ARCHE_PATH}/campaigns/new`}
          className="px-3 py-2 rounded-md bg-primary text-primary-foreground text-sm hover:bg-primary/90"
        >
          New campaign
        </Link>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="rounded-lg border border-border p-4">
          <div className="text-sm text-muted-foreground">Active campaigns</div>
          <div className="text-2xl font-semibold">{active.length}</div>
        </div>
        <div className="rounded-lg border border-border p-4">
          <div className="text-sm text-muted-foreground">All campaigns</div>
          <div className="text-2xl font-semibold">{campaigns.length}</div>
        </div>
        <div className="rounded-lg border border-border p-4">
          <div className="text-sm text-muted-foreground">Active total budgets</div>
          <div className="text-2xl font-semibold">{centsToUsd(totalActiveBudget)}</div>
        </div>
        <div className="rounded-lg border border-border p-4">
          <div className="text-sm text-muted-foreground">Daily ceiling (sum)</div>
          <div className="text-2xl font-semibold">{centsToUsd(dailyCeiling)}</div>
        </div>
      </div>

      <PerformanceReport holderId={holderId} />

      {campaigns.length > 0 && (
        <section>
          <h2 className="text-lg font-medium mb-2">Recent campaigns</h2>
          <ul className="divide-y divide-border rounded-lg border border-border">
            {campaigns.slice(0, 5).map((c) => (
              <li key={c.id}>
                <Link
                  to={`${ARCHE_PATH}/campaigns/${c.id}`}
                  className="flex items-center justify-between px-4 py-3 hover:bg-muted/50"
                >
                  <span className="font-medium">{c.name}</span>
                  <span className="text-sm text-muted-foreground capitalize">{c.status}</span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
